// Admin Analytics Component
// Display analytics overview with key metrics

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { getAnalytics } from '@/services/adminApi';
import { 
  Users, 
  UserPlus, 
  Activity, 
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Briefcase,
  Bug,
  Lightbulb,
  MessageCircle,
  AlertCircle
} from 'lucide-react';
import Icon from '@/components/ui/Icon';
import { toast } from '@/components/ui/use-toast';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Prevent duplicate calls
  const isLoadingRef = useRef(false); // Ref for synchronous loading check

  const loadAnalytics = useCallback(async (retryCount = 0) => {
    // Prevent duplicate calls using ref for synchronous check
    if (isLoadingRef.current) {
      console.log('Analytics already loading, skipping duplicate call');
      return;
    }
    
    isLoadingRef.current = true;
    setIsLoading(true);
    setLoading(true);
    setError(null); // Clear previous errors
    
    try {
      // Use the new admin API service
      const data = await getAnalytics();
      
      if (data.success) {
        setAnalytics(data.analytics || {
          totalUsers: 0,
          activeUsers: 0,
          totalBookings: 0,
          totalFeedback: 0,
          recentActivity: [],
          userGrowth: [],
          popularFeatures: [],
          errorRate: 0
        });
      } else {
        throw new Error(data.message || 'Failed to load analytics');
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError(error.message);
      
      toast({
        title: "Error loading analytics",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setIsLoading(false);
      isLoadingRef.current = false; // Reset ref
    }
  }, []); // Empty dependency array - function only created once

  useEffect(() => {
    // Add a small delay to ensure the proxy is fully ready
    const timer = setTimeout(() => {
      loadAnalytics();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [loadAnalytics]);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear any pending operations
      setIsLoading(false);
      isLoadingRef.current = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="loader-circle" size="xl" className="animate-spin text-brand-600" />
        <span className="ml-2 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">
          <AlertCircle className="w-12 h-12 mx-auto mb-2" />
          <h3 className="text-lg font-semibold">Error Loading Analytics</h3>
          <p className="text-sm text-gray-600 mt-2">{error}</p>
        </div>
        <button
          onClick={() => {
            setError(null);
            loadAnalytics();
          }}
          disabled={isLoading}
          className="bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
        >
          {isLoading ? 'Retrying...' : 'Retry'}
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-8 text-gray-500">
        Failed to load analytics data.
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, trendUp }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {subtitle && (
              <div className="flex items-center gap-1 mt-2">
                {trend !== undefined && (
                  trendUp ? (
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-600" />
                  )
                )}
                <p className="text-sm text-gray-500">{subtitle}</p>
              </div>
            )}
          </div>
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
            <Icon className="w-6 h-6 text-brand-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* User Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4">User Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            title="Total Users"
            value={analytics.users.total}
            subtitle="All registered users"
          />
          <StatCard
            icon={Activity}
            title="Active Users (7d)"
            value={analytics.users.active_last_7_days}
            subtitle={`${analytics.engagement.active_user_rate}% active rate`}
          />
          <StatCard
            icon={UserPlus}
            title="New Users (7d)"
            value={analytics.users.new_last_7_days}
            subtitle={
              analytics.users.growth_rate >= 0 
                ? `+${analytics.users.growth_rate}% growth`
                : `${analytics.users.growth_rate}% decline`
            }
            trend={analytics.users.growth_rate}
            trendUp={analytics.users.growth_rate >= 0}
          />
          <StatCard
            icon={Briefcase}
            title="Total Programs"
            value={analytics.programs.total}
            subtitle="Tracked by all users"
          />
        </div>
      </div>

      {/* Feedback Metrics */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Feedback Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Feedback by Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Feedback by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">New</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">
                    {analytics.feedback.by_status.new}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-yellow-600" />
                    <span className="font-medium">In Progress</span>
                  </div>
                  <span className="text-xl font-bold text-yellow-600">
                    {analytics.feedback.by_status.in_progress}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="font-medium">Resolved</span>
                  </div>
                  <span className="text-xl font-bold text-green-600">
                    {analytics.feedback.by_status.resolved}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-gray-600" />
                    <span className="font-medium">Closed</span>
                  </div>
                  <span className="text-xl font-bold text-gray-600">
                    {analytics.feedback.by_status.closed}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback by Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Feedback by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Bug className="w-4 h-4 text-red-600" />
                    <span className="font-medium">Bug Reports</span>
                  </div>
                  <span className="text-xl font-bold text-red-600">
                    {analytics.feedback.by_type.bug}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-brand-600" />
                    <span className="font-medium">Feature Requests</span>
                  </div>
                  <span className="text-xl font-bold text-brand-600">
                    {analytics.feedback.by_type.feature_request}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">General Feedback</span>
                  </div>
                  <span className="text-xl font-bold text-blue-600">
                    {analytics.feedback.by_type.general}
                  </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-indigo-600" />
                    <span className="font-medium">Total Feedback</span>
                  </div>
                  <span className="text-xl font-bold text-indigo-600">
                    {analytics.feedback.total}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border-l-4 border-green-500 bg-green-50 rounded">
              <p className="font-semibold text-green-900">User Engagement</p>
              <p className="text-sm text-green-700 mt-1">
                {analytics.engagement.active_user_rate}% of users have been active in the last 7 days
              </p>
            </div>
            
            <div className="p-4 border-l-4 border-blue-500 bg-blue-50 rounded">
              <p className="font-semibold text-blue-900">Growth Trend</p>
              <p className="text-sm text-blue-700 mt-1">
                {analytics.users.growth_rate >= 0 ? 'Growing' : 'Declining'} at {Math.abs(analytics.users.growth_rate)}% week over week
              </p>
            </div>
            
            <div className="p-4 border-l-4 border-yellow-500 bg-yellow-50 rounded">
              <p className="font-semibold text-yellow-900">Feedback Pending</p>
              <p className="text-sm text-yellow-700 mt-1">
                {analytics.feedback.by_status.new + analytics.feedback.by_status.in_progress} feedback items need attention
              </p>
            </div>
            
            <div className="p-4 border-l-4 border-purple-500 bg-purple-50 rounded">
              <p className="font-semibold text-purple-900">Programs Tracked</p>
              <p className="text-sm text-brand-700 mt-1">
                Average of {analytics.users.total > 0 ? (analytics.programs.total / analytics.users.total).toFixed(1) : 0} programs per user
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timestamp */}
      <p className="text-xs text-gray-500 text-center">
        Last updated: {new Date().toLocaleString()}
      </p>
    </div>
  );
};

export default AdminAnalytics;
